import { Box, Button, CardMedia, CircularProgress, Container, Divider, ListItem, Menu, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useState } from "react";
import useGetPipelinesByEmail from "../../shared/hooks/useGetPipelinesByEmail";
import { useEvalPipelineMutation, useSubmitPipelineMutation } from "../../store/processing/processingSlice";
import { useGetAssignmentQuery } from "../../store/service/serviceSlice";
import HintButton from "./HintButton";
import HintDrawer from "./HintDrawer";
import { Trans, useTranslation } from "react-i18next";

type Props = {
    assignment_title: string;
}

type Selected = {
    selectedassignment_title: string;
    selectedPid: string;
}

type Score = {
    ged: number;
    diff: number;
    score: number;
    img: string;
    img1: string;
    img2: string;
}

const Assignment = ({ assignment_title }: Props): JSX.Element => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null as null | HTMLElement);
    const [menu, setMenu] = useState(null as null | number);
    const [selected1, setSelected1] = useState(null as null | Selected);
    const [selected2, setSelected2] = useState(null as null | Selected);
    const [buttonLabel1, setButtonLabel1]  = useState(t("Select Pipeline") as string);
    const [buttonLabel2, setButtonLabel2]  = useState("Set Pipeline 2" as string);
    const [unusedHints, setUnusedHints] = useState([] as number[]);
    const [evalPipeline] = useEvalPipelineMutation();
    const [submitPipeline] = useSubmitPipelineMutation();
    const open = Boolean(anchorEl);
    const pipelines = useGetPipelinesByEmail();
    const assignment = useGetAssignmentQuery(assignment_title);
    const [score, setScore] = useState(null as null | Score);

    useEffect(()=> {
        if(assignment.data) {
            const newUnusedHints: number[] = [];
            assignment.data.tasks.forEach(task => {
                newUnusedHints.push(task.hints.length)
            });
            setUnusedHints(newUnusedHints);
        }
    }, [assignment.data]);

    const onHintButtonClick = (task_index: number) => {
        const newUnusedHints = [...unusedHints];
        newUnusedHints[task_index] = newUnusedHints[task_index] - 1;
        setUnusedHints(newUnusedHints);
    }

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        if(event.currentTarget.id == "Menu-Button1") {
            setMenu(1);
        }
        else { setMenu(2); }
    }

    const handleClose = (assignment_title: string, pid: string) => {
        setAnchorEl(null);
        if(assignment_title === "" && pid === "") {
            if(menu === 1) {
                setButtonLabel1("Set Pipeline 1")
                setSelected1(null);
            }
            if(menu === 2) {
                setButtonLabel2("Set Pipeline 2")
                setSelected2(null);
            }
        }
        else {
            if(menu === 1) {
                setButtonLabel1(assignment_title)
                setSelected1({selectedassignment_title: assignment_title, selectedPid: pid})
            }
            if(menu === 2) {
                setButtonLabel2(assignment_title)
                setSelected2({selectedassignment_title: assignment_title, selectedPid: pid})
            }
        }
        setMenu(null);
    }

    async function onEval() {
        const score = await submitPipeline({ pid: selected1?.selectedPid, assignment_title: assignment_title});
        if ('data' in score) {
          const actual_score = score.data as Score;
          setScore({ged: Math.round(actual_score.ged*100)/100, diff: Math.round(actual_score.diff*10000)/10000, score: Math.round(actual_score.score*100)/100, img: actual_score.img, img1: actual_score.img1, img2: actual_score.img2});
        }
      }

    return (
        <Paper elevation={2} sx={{ px: 2, py: 2}}>
            <Typography variant={'h6'}>
                <strong>{assignment_title}</strong>
            </Typography>
            <Stack sx={{width: '80%'}} direction={'column'} justifyContent={'center'} spacing={2} divider={<Divider orientation="horizontal" flexItem />}>
                {!assignment.isLoading && assignment.data ? (
                    assignment.data.tasks.map((task, task_index) => (
                        <Stack sx={{width: '80%'}} direction={'column'} justifyContent={'center'} spacing={1.5}>
                            <Stack direction={'row'}>
                                <Typography variant={'h6'}>
                                    {task.title}
                                </Typography>
                                <HintButton availableHints={unusedHints[task_index]} task_index={task_index} onClick={onHintButtonClick}/>
                            </Stack>
                            <Typography>
                                {task.description}
                            </Typography>
                            <HintDrawer hints={task.hints} unlockedHints={task.hints.length - unusedHints[task_index]}></HintDrawer>
                        </Stack>
                    ))
                ) : (
                    <Box sx={{ p: 2 }}>
                        <CircularProgress />
                    </Box>
                )
                }
                <Box>
                    <Typography variant={'h6'}>
                        <strong><Trans>Submission</Trans></strong>
                    </Typography>
                    <Button
                        id="Menu-Button1"
                        variant={'outlined'}
                        aria-controls={open ? 'menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                    >
                        {buttonLabel1}
                    </Button>

                    {/*und

                    <Button
                        id="Menu-Button2"
                        variant={'outlined'}
                        aria-controls={open ? 'menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleClick}
                    >
                        {buttonLabel2}
                    </Button> */}
                    <Button
                        variant={'contained'}
                        color={'success'}
                        onClick={onEval}
                        disabled={true}
                    >
                        <Trans>Submit</Trans>
                    </Button>
                    {score !== null ? (
                        <Box>
                        <Typography>
                            Graph edit distance: {score.ged}
                        </Typography>
                        <Typography>
                            Difference: {score.diff * 100.0}%
                        </Typography>
                        <Typography>
                            Score: {score.score}/20  (Score = (20-ged)*Difference)
                        </Typography>
                        <CardMedia 
                            component="img"
                            src={`data:image/png;base64,${score.img}`}
                            alt="result_image"
                            sx={{ padding: "1em 1em 1em 1em" }}
                        />
                        <CardMedia 
                            component="img"
                            src={`data:image/png;base64,${score.img1}`}
                            alt="result_image"
                            sx={{ padding: "1em 1em 1em 1em" }}
                        />
                        <CardMedia 
                            component="img"
                            src={`data:image/png;base64,${score.img2}`}
                            alt="result_image"
                            sx={{ padding: "1em 1em 1em 1em" }}
                        />
                        </Box>

                    ) : (<></>)}
                    <Menu 
                        id="menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={()=>{handleClose("","")}}
                        MenuListProps={{ 'aria-labelledby': 'Menu-Button1' }}
                    >
                        {!pipelines.isLoading && pipelines.data ? (
                           pipelines.data.map(entry => (
                            <MenuItem onClick={()=>{handleClose(entry.pipeline.name, entry.pid)}}>{entry.pipeline.name}</MenuItem>
                           )) 
                        ) : (
                            <Box sx={{ p: 2 }}>
                              <CircularProgress />
                            </Box>
                          )}
                    </Menu>
                </Box>
            </Stack>
        </Paper>
    )
}

export default Assignment;